import axios from "axios";
import { getToken } from "./userService";

const API_BASE_URL = "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

/**
 * Format date to MySQL DATETIME format (YYYY-MM-DD HH:MM:SS)
 */
function formatMySQLDateTime(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Get developer dashboard overview
 */
export async function getDeveloperStudios() {
  const response = await axios.get(`${API_BASE_URL}/developer/studios`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
  });
  return response.data?.developers || [];
}

export async function getDeveloperDashboard(developerId) {
  try {
    const response = await axios.get(`${API_BASE_URL}/developer/dashboard/${developerId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
    });
    return response.data;
  } catch (error) {
    // console.error("Error fetching developer dashboard:", error);
    return null;
  }
}

/**
 * Get all games for a developer
 */
export async function getDeveloperGames(developerId, filters = {}) {
  try {
    const response = await axios.get(`${API_BASE_URL}/developer/games/${developerId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
      params: filters,
    });
    return response.data?.games || response.data || [];
  } catch (error) {
    // console.error("Error fetching developer games:", error);
    return [];
  }
}

function hasEnteredAccessCredentials(accessCredentials) {
  if (accessCredentials == null || accessCredentials === "") {
    return false;
  }
  if (typeof accessCredentials === "string") {
    const trimmed = accessCredentials.trim();
    if (!trimmed) {
      return false;
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return false;
      }
      return Object.keys(parsed).length > 0;
    } catch {
      return true;
    }
  }
  if (typeof accessCredentials !== "object" || Array.isArray(accessCredentials)) {
    return false;
  }
  return Object.keys(accessCredentials).length > 0;
}

function buildCreateGamePayload(gameData) {
  return {
    name: gameData.name,
    genre: gameData.genre,
    description: gameData.description,
    buildType: gameData.buildType,
    playtesterTier: Number(gameData.playtesterTier),
    numPlaytesters: Number(gameData.numPlaytesters),
    deadline: formatMySQLDateTime(gameData.deadline),
    requirements:
      Array.isArray(gameData.requirements) && gameData.requirements.length > 0
        ? JSON.stringify(gameData.requirements)
        : null,
    playtestType: gameData.playtestType || undefined,
    sessionHours:
      gameData.sessionHours != null ? Number(gameData.sessionHours) : undefined,
    lockTotalPlaytime: Boolean(gameData.lockTotalPlaytime),
    isPublicPlaytest: Boolean(gameData.isPublicPlaytest),
  };
}

/**
 * Create a new game
 */
export async function createGame(developerId, gameData) {
  try {
    if (gameData.buildType === "file" && gameData.buildFileUrl) {
      const response = await axios.post(
        `${API_BASE_URL}/developer/games/${developerId}/create`,
        {
          ...buildCreateGamePayload(gameData),
          buildFileUrl: gameData.buildFileUrl,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${getToken()}`,
          },
        }
      );

      return response.data?.game || response.data;
    }

    if (gameData.buildType === "web") {
      const response = await axios.post(
        `${API_BASE_URL}/developer/games/${developerId}/create`,
        {
          ...buildCreateGamePayload(gameData),
          webUrl: gameData.webUrl,
          accessToken: gameData.accessToken || undefined,
          accessCredentials: hasEnteredAccessCredentials(gameData.accessCredentials)
            ? JSON.stringify(gameData.accessCredentials)
            : undefined,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${getToken()}`,
          },
        }
      );

      return response.data?.game || response.data;
    }

    const payload = buildCreateGamePayload(gameData);
    const formData = new FormData();
    
    // Add basic game info
    formData.append('name', payload.name);
    formData.append('genre', payload.genre);
    formData.append('description', payload.description);
    formData.append('buildType', payload.buildType);
    formData.append('playtesterTier', payload.playtesterTier);
    formData.append('numPlaytesters', payload.numPlaytesters);
    formData.append('deadline', payload.deadline);
    if (payload.playtestType) {
      formData.append('playtestType', payload.playtestType);
    }
    if (payload.sessionHours != null) {
      formData.append('sessionHours', payload.sessionHours);
    }
    formData.append('lockTotalPlaytime', payload.lockTotalPlaytime ? 'true' : 'false');
    formData.append('isPublicPlaytest', payload.isPublicPlaytest ? 'true' : 'false');
    
    // Add build-specific data
    if (gameData.buildType === 'file' && gameData.buildFile) {
      formData.append('buildFile', gameData.buildFile);
    } else if (gameData.buildType === 'web') {
      formData.append('webUrl', gameData.webUrl);
      if (gameData.accessToken) {
        formData.append('accessToken', gameData.accessToken);
      }
      if (hasEnteredAccessCredentials(gameData.accessCredentials)) {
        formData.append('accessCredentials', JSON.stringify(gameData.accessCredentials));
      }
    }
    
    // Add requirements
    if (gameData.requirements && Array.isArray(gameData.requirements)) {
      formData.append('requirements', JSON.stringify(gameData.requirements));
    }
    
    const response = await axios.post(
      `${API_BASE_URL}/developer/games/${developerId}/create`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    
    return response.data?.game || response.data;
  } catch (error) {
    // console.error("Error creating game:", error);
    throw error;
  }
}

/**
 * Get a signed upload URL for a game build
 */
export async function getBuildUploadUrl(developerId, { fileName, contentType }, gameId = null) {
  try {
    const endpoint = gameId
      ? `${API_BASE_URL}/developer/games/${developerId}/${gameId}/builds/upload-url`
      : `${API_BASE_URL}/developer/games/${developerId}/builds/upload-url`;

    const response = await axios.post(
      endpoint,
      { fileName, contentType },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("Error fetching build upload URL:", error);
    throw error;
  }
}

/**
 * Update game details
 */
export async function updateGame(developerId, gameId, gameData) {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/developer/games/${developerId}/${gameId}`,
      gameData,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    // console.error("Error updating game:", error);
    throw error;
  }
}

/**
 * Get game details
 */
export async function getGameDetails(developerId, gameId) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/developer/games/${developerId}/${gameId}`,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    // console.error("Error fetching game details:", error);
    return null;
  }
}

/**
 * Get playtesters assigned to a game
 */
export async function getGamePlaytesters(developerId, gameId) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/developer/games/${developerId}/${gameId}/playtesters`,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data?.playtesters || response.data || [];
  } catch (error) {
    // console.error("Error fetching game playtesters:", error);
    return [];
  }
}

/**
 * Get all feedback for a game
 */
export async function getGameFeedback(developerId, gameId, filters = {}) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/developer/games/${developerId}/${gameId}/feedback`,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
        params: filters,
      }
    );
    return response.data?.feedback || response.data || [];
  } catch (error) {
    // console.error("Error fetching game feedback:", error);
    return [];
  }
}

/**
 * Get game analytics / report packages for a developer game.
 *
 * Expected response (report package model):
 * - reportPackages: ready packages from game_report_packages (newest / isLatest first)
 * - reportPackage: singular latest package (legacy compatibility)
 * - reportArtifacts: artifacts with packageId, sortOrder ASC; JSON is preferred for native views, HTML is fallback
 *
 * Legacy fallback (optional):
 * - feedbackReport: { url, generatedAt } single HTML report URL
 *
 * @param {string|number} developerId
 * @param {string|number} gameId
 * @param {{ packageId?: string|number }} [options] - optional packageId to load a specific package
 * @returns {Promise<object|null>}
 */
export async function getGameAnalytics(developerId, gameId, options = {}) {
  try {
    const params = {};
    if (options.packageId != null && options.packageId !== "") {
      params.packageId = options.packageId;
    }

    const response = await axios.get(
      `${API_BASE_URL}/developer/games/${developerId}/${gameId}/analytics`,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
        params,
      }
    );
    return response.data;
  } catch (error) {
    // console.error("Error fetching game analytics:", error);
    return null;
  }
}

/**
 * Fetch JSON report artifact contents. Proxied through the backend to avoid GCS CORS.
 */
export async function getReportArtifactJson(developerId, gameId, artifactId) {
  if (!developerId || !gameId || !artifactId) return null;

  const response = await axios.get(
    `${API_BASE_URL}/developer/games/${developerId}/${gameId}/report-artifacts/${artifactId}`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
    }
  );
  return response.data;
}

export async function fetchReportJson(artifact, { developerId, gameId } = {}) {
  if (!artifact?.safeUrl && !artifact?.jsonArtifactId) {
    throw new Error("Report JSON URL is missing.");
  }

  if (artifact.safeUrl) {
    try {
      const response = await fetch(artifact.safeUrl, { mode: "cors" });
      if (response.ok) {
        return response.json();
      }
    } catch (error) {
      // Browser CORS or network failure; try the authenticated backend proxy next.
    }
  }

  if (developerId && gameId && artifact.jsonArtifactId && !String(artifact.jsonArtifactId).startsWith("exec-from-")) {
    const proxied = await getReportArtifactJson(developerId, gameId, artifact.jsonArtifactId);
    if (proxied) return proxied;
  }

  throw new Error("Failed to load report JSON.");
}

/**
 * Get developer analytics overview
 */
export async function getDeveloperAnalytics(developerId) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/developer/analytics/${developerId}`,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    // console.error("Error fetching developer analytics:", error);
    return null;
  }
}

/**
 * Export feedback report
 */
export async function exportFeedbackReport(developerId, gameId, format = 'pdf') {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/developer/games/${developerId}/${gameId}/feedback/export`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
        params: { format },
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    // console.error("Error exporting feedback report:", error);
    throw error;
  }
}

/**
 * Auto-assign playtesters to a game
 */
export async function autoAssignPlaytesters(developerId, gameId) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/developer/games/${developerId}/${gameId}/assign-playtesters`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    // console.error("Error auto-assigning playtesters:", error);
    throw error;
  }
}

/**
 * Generate feedback report for a build
 * @param {number} developerId - The developer's user ID
 * @param {number} gameId - The game ID
 * @param {number} buildId - The build ID to generate report for
 * @returns {Promise<Object>} Report generation response with reportUrl
 */
export async function generateBuildReport(developerId, gameId, buildId) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/developer/games/${developerId}/${gameId}/builds/${buildId}/report`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    // console.error("Error generating build report:", error);
    throw error;
  }
}







